/** Usage & cost queries — tenant-scoped, per call. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type CallCost, callCostSchema, type UsageEntry, usageEntrySchema } from '../responses';
import { unwrap } from './_helpers';

/** `GET /calls/:callId/usage` — usage ledger entries for a call. */
export function useCallUsage(tenantId: string, callId: string): UseQueryResult<UsageEntry[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.callUsage(tenantId, callId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/calls/{callId}/usage', {
        params: { path: { tenantId, callId } },
        parseAs: 'json',
      });
      return unwrap(result, z.array(usageEntrySchema));
    },
    enabled: callId.length > 0,
  });
}

/** `GET /calls/:callId/usage/cost` — rolled-up cost for a call. */
export function useCallCost(tenantId: string, callId: string): UseQueryResult<CallCost> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.callCost(tenantId, callId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/calls/{callId}/usage/cost', {
        params: { path: { tenantId, callId } },
        parseAs: 'json',
      });
      return unwrap(result, callCostSchema);
    },
    enabled: callId.length > 0,
  });
}
